import React from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import { bookingShape, passengerShape } from 'constants/propTypes';

import { FormWrapper } from './CaptureDetail.styles';
import {
  ColumnContainer,
  DetailsContainer,
  ErrorMessage,
  FormContainer,
  RowContainer,
  StyledForm,
  StyledInputFields,
  StyledLabel,
  StyledSelect,
  StyledAdditional,
  InfoIcon
} from './CaptureDetailForm.styles';
import { FormattedMessage, injectIntl } from 'react-intl';
import { TextInput } from '@eurostar/ui-components';
import { ConfirmButton } from '../../../components/ui/Button';
import { useMutation } from '@apollo/client';
import moment from 'moment';
import { ADVANCED_PAX_INFO_DATE_FORMAT } from '../../../constants/passengers';
import countries from '../../../constants/countriesiso3';
import PropTypes from 'prop-types';
import { CAPTURE_ADVANCED_PASSENGER_INFORMATION } from '../../../store/mutations';
import AgentPortalError from '../../../errors/AgentPortalError';
import { ACTION_CODES } from '../../../constants/errors';
import { buildCaptureAdvancedPassengerInformationVariables } from './CaptureTravelDocuments.utils';
import { getLatestArrivalTimeForInfant } from 'utils/getLatestArrivalTimeForInfant';

const CaptureDetailForm = ({ booking, hasTravelDocumentBeenProvided, passenger, intl, refreshTravelDocs }) => {
  const isInfantPassenger = passenger.isInfant === true;

  const [
    captureAdvancedPassengerInformation,
    { loading: isCaptureAdvancedPassengerInformationLoading, error: captureAdvancedPassengerInformationError }
  ] = useMutation(CAPTURE_ADVANCED_PASSENGER_INFORMATION);

  let latestTravelDate = moment();

  (booking.outbound ?? []).concat(booking.inbound || []).forEach((l) => {
    if (!l.products.find((p) => p.isActive && !p.isCancelled && p.passenger.id === passenger.id)) {
      return;
    }

    if (latestTravelDate.diff(moment(l.timing.isoDate)) < 0) {
      latestTravelDate = moment(l.timing.isoDate);
    }
  });

  latestTravelDate.add(1, 'd');

  const loading = isCaptureAdvancedPassengerInformationLoading;

  const defaultValues = {
    dateOfBirth: '',
    documentNumber: '',
    familyName: '',
    givenName: '',
    gender: '',
    nationality: 'GB',
    documentType: 'passport',
    countryOfIssue: 'GB',
    expiryDate: ''
  };

  defaultValues.givenName = passenger.firstName;
  defaultValues.familyName = passenger.lastName;

  if (passenger.birthDate) {
    defaultValues.dateOfBirth = passenger.birthDate;
  }

  const formMethods = useForm({
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues
  });

  const { register, handleSubmit, formState, getValues, setValue } = formMethods;

  const { errors } = formState;

  const formDob = getValues().dateOfBirth;

  const onHandleCalendarChange2 = (event) => {
    setValue(event.target.name, event.target.value, {
      shouldValidate: false
    });
  };

  const dateInvalid = (date) => {
    const dateObject = moment(date, ADVANCED_PAX_INFO_DATE_FORMAT);

    if (!dateObject.isValid()) {
      return true;
    }

    return false;
  };

  const dateOfBirthInvalidForInfant = (date) => {
    if (!isInfantPassenger) return false;

    const dateObject = moment(date, ADVANCED_PAX_INFO_DATE_FORMAT);
    const getLatestArrivalTrains = getLatestArrivalTimeForInfant({ guardianID: passenger.id, booking });

    if (getLatestArrivalTrains === null) return false;
    const diffInYears = getLatestArrivalTrains.diff(dateObject, 'year');

    return diffInYears >= 4 || diffInYears < 0;
  };

  const onSubmit = async (data) => {
    await captureAdvancedPassengerInformation({
      variables: buildCaptureAdvancedPassengerInformationVariables({ booking, passenger, data })
    });

    refreshTravelDocs();
  };

  if (captureAdvancedPassengerInformationError) {
    throw new AgentPortalError(captureAdvancedPassengerInformationError, ACTION_CODES.DEFAULT_ACTION, {});
  }

  return (
    <FormWrapper>
      <FormProvider {...formMethods}>
        <StyledForm data-testid="buy-thalys-pass-form" onSubmit={handleSubmit(onSubmit)}>
          <DetailsContainer>
            <FormContainer>
              <RowContainer>
                <ColumnContainer>
                  <StyledInputFields>
                    <StyledLabel mandatory htmlFor="documentType">
                      <FormattedMessage id="advancedPaxInfo.documentType" />
                    </StyledLabel>
                    <StyledSelect
                      {...register('documentType', {
                        required: 'advancedPaxInfo.invalidDocumentType'
                      })}
                      id="documentType"
                    >
                      <option key="passport" value="passport">
                        {intl.formatMessage({ id: 'advancedPaxInfo.passport' })}
                      </option>
                      <option key="identity card - a" value="identity card - a">
                        {intl.formatMessage({ id: 'advancedPaxInfo.idCard' })}
                      </option>
                    </StyledSelect>
                    {errors.documentType && (
                      <ErrorMessage>
                        <FormattedMessage id={errors.documentType.message} />
                      </ErrorMessage>
                    )}
                  </StyledInputFields>

                  <StyledInputFields>
                    <StyledLabel mandatory htmlFor="documentNumber">
                      <FormattedMessage id="advancedPaxInfo.documentNumber" />
                    </StyledLabel>
                    <TextInput
                      name="documentNumber"
                      id="documentNumber"
                      type="text"
                      {...register('documentNumber', {
                        required: 'advancedPaxInfo.invalidDocumentNumber'
                      })}
                    />
                    {errors.documentNumber && (
                      <ErrorMessage>
                        <FormattedMessage id={errors.documentNumber.message} />
                      </ErrorMessage>
                    )}
                  </StyledInputFields>

                  <StyledInputFields>
                    <StyledLabel mandatory htmlFor="countryOfIssue">
                      <FormattedMessage id="advancedPaxInfo.documentCountry" />
                    </StyledLabel>
                    <StyledAdditional>
                      <InfoIcon>&#9432;</InfoIcon>
                      <FormattedMessage id="advancedPaxInfo.documentCountryAdditionalInfo" />
                    </StyledAdditional>
                    <StyledSelect
                      {...register('countryOfIssue', {
                        required: 'advancedPaxInfo.invalidDocumentCountry'
                      })}
                      id="countryOfIssue"
                    >
                      {countries.map(({ iso3, name }) => (
                        <option key={iso3} value={iso3}>
                          {name}
                        </option>
                      ))}
                    </StyledSelect>
                    {errors.countryOfIssue && (
                      <ErrorMessage>
                        <FormattedMessage id={errors.countryOfIssue.message} />
                      </ErrorMessage>
                    )}
                  </StyledInputFields>

                  <StyledInputFields>
                    <StyledLabel mandatory htmlFor="expiryDate">
                      <FormattedMessage id="advancedPaxInfo.expiryDate" />
                    </StyledLabel>
                    <TextInput
                      id="expiryDate"
                      type="date"
                      defaultValue={latestTravelDate.format(ADVANCED_PAX_INFO_DATE_FORMAT).toString()}
                      min={latestTravelDate.format(ADVANCED_PAX_INFO_DATE_FORMAT).toString()}
                      {...register('expiryDate', {
                        required: 'advancedPaxInfo.invalidExpiryDate',
                        validate: (value) => (value && !dateInvalid(value) ? true : 'advancedPaxInfo.invalidExpiryDate'),
                        onChange: onHandleCalendarChange2
                      })}
                    />
                    {errors.expiryDate && (
                      <ErrorMessage>
                        <FormattedMessage id={errors.expiryDate.message} />
                      </ErrorMessage>
                    )}
                  </StyledInputFields>
                </ColumnContainer>

                <ColumnContainer>
                  <StyledInputFields>
                    <StyledLabel mandatory htmlFor="givenName">
                      <FormattedMessage id="advancedPaxInfo.firstName" />
                    </StyledLabel>

                    <StyledAdditional>
                      <InfoIcon>&#9432;</InfoIcon>
                      <FormattedMessage id="advancedPaxInfo.firstNameAdditionalInfo" />
                    </StyledAdditional>

                    <TextInput
                      id="givenName"
                      type="text"
                      {...register('givenName', {
                        required: 'advancedPaxInfo.invalidFirstName'
                      })}
                    />
                    {errors.givenName && (
                      <ErrorMessage>
                        <FormattedMessage id={errors.givenName.message} />
                      </ErrorMessage>
                    )}
                  </StyledInputFields>

                  <StyledInputFields>
                    <StyledLabel mandatory htmlFor="familyName">
                      <FormattedMessage id="advancedPaxInfo.lastName" />
                    </StyledLabel>
                    <TextInput
                      id="familyName"
                      type="text"
                      {...register('familyName', {
                        required: 'advancedPaxInfo.invalidLastName'
                      })}
                    />
                    {errors.familyName && (
                      <ErrorMessage>
                        <FormattedMessage id={errors.familyName.message} />
                      </ErrorMessage>
                    )}
                  </StyledInputFields>

                  <StyledInputFields>
                    <StyledLabel mandatory htmlFor="nationality">
                      <FormattedMessage id="advancedPaxInfo.nationality" />
                    </StyledLabel>
                    <StyledSelect
                      {...register('nationality', {
                        required: 'advancedPaxInfo.invalidNationality'
                      })}
                      id="nationality"
                    >
                      {countries.map(({ iso3, name }) => (
                        <option key={iso3} value={iso3}>
                          {name}
                        </option>
                      ))}
                    </StyledSelect>
                    {errors.nationality && (
                      <ErrorMessage>
                        <FormattedMessage id={errors.nationality.message} />
                      </ErrorMessage>
                    )}
                  </StyledInputFields>

                  <StyledInputFields>
                    <StyledLabel mandatory htmlFor="gender">
                      <FormattedMessage id="advancedPaxInfo.gender" />
                    </StyledLabel>
                    <StyledSelect
                      {...register('gender', {
                        required: 'advancedPaxInfo.invalidGender'
                      })}
                      id="gender"
                    >
                      <option key="M" value="M">
                        {intl.formatMessage({ id: 'advancedPaxInfo.male' })}
                      </option>
                      <option key="F" value="F">
                        {intl.formatMessage({ id: 'advancedPaxInfo.female' })}
                      </option>
                      <option key="U" value="U">
                        {intl.formatMessage({ id: 'advancedPaxInfo.undisclosed' })}
                      </option>
                      <option key="X" value="X">
                        {intl.formatMessage({ id: 'advancedPaxInfo.unspecified' })}
                      </option>
                    </StyledSelect>
                    {errors.gender && (
                      <ErrorMessage>
                        <FormattedMessage id={errors.gender.message} />
                      </ErrorMessage>
                    )}
                  </StyledInputFields>

                  <StyledInputFields>
                    <StyledLabel mandatory htmlFor="dateOfBirth">
                      <FormattedMessage id="advancedPaxInfo.dob" />
                    </StyledLabel>
                    <TextInput
                      id="dateOfBirth"
                      type="date"
                      defaultValue={formDob ? moment(formDob).format(ADVANCED_PAX_INFO_DATE_FORMAT).toString() : ''}
                      max={moment().format(ADVANCED_PAX_INFO_DATE_FORMAT).toString()}
                      {...register('dateOfBirth', {
                        required: 'advancedPaxInfo.invalidDob',
                        validate: (value) =>
                          value && !dateInvalid(value)
                            ? value && !dateOfBirthInvalidForInfant(value)
                              ? true
                              : 'advancedPaxInfo.invalidDobInfant'
                            : 'advancedPaxInfo.invalidDob',
                        onChange: onHandleCalendarChange2
                      })}
                    />
                    {errors.dateOfBirth && (
                      <ErrorMessage>
                        <FormattedMessage id={errors.dateOfBirth.message} />
                      </ErrorMessage>
                    )}
                  </StyledInputFields>
                </ColumnContainer>
              </RowContainer>
            </FormContainer>
          </DetailsContainer>

          <ConfirmButton
            type="submit"
            data-cy="capture-travel-document-submit"
            style={{ width: 'min-content' }}
            loading={loading}
            disabled={!formMethods.formState.isValid || loading}
          >
            <FormattedMessage id={hasTravelDocumentBeenProvided ? 'advancedPaxInfo.saveAgain' : 'advancedPaxInfo.save'} />
          </ConfirmButton>
        </StyledForm>
      </FormProvider>
    </FormWrapper>
  );
};

CaptureDetailForm.propTypes = {
  booking: bookingShape,
  passenger: passengerShape,
  refreshTravelDocs: PropTypes.func.isRequired,
  hasTravelDocumentBeenProvided: PropTypes.bool.isRequired,
  intl: PropTypes.shape({
    formatMessage: PropTypes.func.isRequired
  }).isRequired
};

export default injectIntl(CaptureDetailForm);
